import {
  db,
  formKey,
  cardStateKey,
  makeDirection,
  type Concept,
  type ConceptForm,
  type CardState,
  type DeckRow,
} from "./db";
import type { LanguageId } from "./references";

/**
 * One-shot migration: read the pre-IndexedDB `lexibit-data` (or legacy
 * `exibit-data`) blob out of localStorage, fan it out across the new
 * tables, and mark the migration done in `kv`. Safe to call on every
 * boot — it no-ops after the first successful run.
 *
 * The original localStorage entry is kept intact until a follow-up version
 * confirms IndexedDB writes survived; nothing here deletes user data.
 */

const STORAGE_KEY = "lexibit-data";
const LEGACY_STORAGE_KEY = "exibit-data";
const MIGRATION_FLAG = "migration:localStorage:v1";

type LegacyCard = {
  id: string;
  deckId: string;
  front: string;
  back: string;
  nextReview: number;
  interval: number;
  repetition: number;
  efactor: number;
  entityType?: string;
  frequency?: number;
  classification?: {
    topic?: string;
    ageGroup?: string;
    level?: string;
  };
};

type LegacyDeck = {
  id: string;
  name: string;
  createdAt: number;
  sourceLang?: string;
  targetLang?: string;
  classification?: {
    topic?: string;
    ageGroup?: string;
    level?: string;
  };
};

type LegacyState = {
  profile?: unknown;
  decks?: LegacyDeck[];
  cards?: LegacyCard[];
  settings?: unknown;
  activity?: { date: string; reviewed: number; correct: number }[];
};

/**
 * Fallback language used when a legacy deck has no source/target set.
 * "und" = ISO 639-2 "Undetermined" — keeps the data importable without
 * pretending to know what language the strings are in.
 */
const UNKNOWN_LANG = "und" as LanguageId;

export async function migrateFromLocalStorageIfNeeded(): Promise<void> {
  const flag = await db.kv.get(MIGRATION_FLAG);
  if (flag) return;

  const raw =
    localStorage.getItem(STORAGE_KEY) ??
    localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!raw) {
    await db.kv.put({ id: MIGRATION_FLAG, value: { at: Date.now(), skipped: "no-data" } });
    return;
  }

  let parsed: LegacyState;
  try {
    parsed = JSON.parse(raw) as LegacyState;
  } catch {
    // Don't block the app on a corrupt blob — leave it in localStorage for
    // forensic inspection and mark migration done.
    await db.kv.put({ id: MIGRATION_FLAG, value: { at: Date.now(), skipped: "parse-error" } });
    return;
  }

  const legacyDecks = parsed.decks ?? [];
  const legacyCards = parsed.cards ?? [];
  const activity = parsed.activity ?? [];

  const decksById = new Map(legacyDecks.map((d) => [d.id, d]));
  const concepts: Concept[] = [];
  const forms: ConceptForm[] = [];
  const cardStates: CardState[] = [];

  for (const lc of legacyCards) {
    const deck = decksById.get(lc.deckId);
    const sourceLang = (deck?.sourceLang ?? UNKNOWN_LANG) as LanguageId;
    const targetLang = (deck?.targetLang ?? UNKNOWN_LANG) as LanguageId;

    // Synthetic concept per legacy card — we don't have enough info to
    // dedupe across decks safely (same word may have different translations
    // in different decks). Pack id "legacy" marks these for later cleanup.
    const conceptId = `legacy:${lc.id}`;
    concepts.push({
      id: conceptId,
      entityType: (lc.entityType as Concept["entityType"]) ?? "word",
      frequency: lc.frequency,
      topic: (lc.classification?.topic ?? deck?.classification?.topic) as Concept["topic"],
      ageGroup: (lc.classification?.ageGroup ??
        deck?.classification?.ageGroup) as Concept["ageGroup"],
      level: (lc.classification?.level ?? deck?.classification?.level) as Concept["level"],
      packId: "legacy",
    });

    forms.push(
      {
        key: formKey(conceptId, sourceLang),
        conceptId,
        lang: sourceLang,
        texts: [lc.front],
      },
      {
        key: formKey(conceptId, targetLang),
        conceptId,
        lang: targetLang,
        texts: [lc.back],
      },
    );

    const direction = makeDirection(sourceLang, targetLang);
    cardStates.push({
      key: cardStateKey(conceptId, direction),
      conceptId,
      direction,
      deckId: lc.deckId,
      nextReview: lc.nextReview,
      interval: lc.interval,
      repetition: lc.repetition,
      efactor: lc.efactor,
    });
  }

  const deckRows: DeckRow[] = legacyDecks.map((d) => ({
    id: d.id,
    name: d.name,
    createdAt: d.createdAt,
    sourceLang: d.sourceLang as LanguageId | undefined,
    targetLang: d.targetLang as LanguageId | undefined,
    topic: d.classification?.topic as DeckRow["topic"],
    ageGroup: d.classification?.ageGroup as DeckRow["ageGroup"],
    level: d.classification?.level as DeckRow["level"],
  }));

  await db.transaction(
    "rw",
    [db.concepts, db.conceptForms, db.cardStates, db.decks, db.activity, db.kv],
    async () => {
      if (deckRows.length) await db.decks.bulkPut(deckRows);
      if (concepts.length) await db.concepts.bulkPut(concepts);
      if (forms.length) await db.conceptForms.bulkPut(forms);
      if (cardStates.length) await db.cardStates.bulkPut(cardStates);
      if (activity.length) await db.activity.bulkPut(activity);
      if (parsed.settings) {
        await db.kv.put({ id: "settings", value: parsed.settings });
      }
      if (parsed.profile) {
        await db.kv.put({ id: "profile", value: parsed.profile });
      }
      await db.kv.put({
        id: MIGRATION_FLAG,
        value: {
          at: Date.now(),
          counts: {
            decks: deckRows.length,
            concepts: concepts.length,
            forms: forms.length,
            cardStates: cardStates.length,
            activity: activity.length,
          },
        },
      });
    },
  );
}
